import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import type {
    ListTeaTypesParams,
    UpsertTeaTypeRequest,
} from '@/lib/api/tea-types-api'
import { teaTypesApi } from '@/lib/api/tea-types-api'
import { ApiError } from '@/lib/api/client'
import { m } from '@/lib/paraglide/messages'

export const teaTypeKeys = {
    all: ['tea-types'] as const,
    lists: () => [...teaTypeKeys.all, 'list'] as const,
    list: (params: ListTeaTypesParams) =>
        [...teaTypeKeys.lists(), params] as const,
    detail: (id: string) => [...teaTypeKeys.all, 'detail', id] as const,
}

export function useTeaTypes(params?: ListTeaTypesParams) {
    return useQuery({
        queryKey: teaTypeKeys.list(params ?? {}),
        queryFn: async () => {
            const res = await teaTypesApi.list(params)
            return { data: res.data ?? [], meta: res.meta }
        },
    })
}

/**
 * Danh mục giống chè còn dùng được, dùng chung cho MỌI nơi cần mời người dùng
 * chọn giống: bộ lọc danh sách, form tạo phiếu, và ô sửa tại chỗ trên bảng.
 *
 * Tồn tại vì tham số truy vấn chính là khoá cache: hai nơi hỏi cùng một danh
 * mục nhưng khác một tham số sẽ thành hai khoá khác nhau và bắn hai request
 * gần như trùng nhau. Gom về một hàm thì không còn chỗ nào lệch được nữa.
 *
 * `per_page` đặt đủ rộng để danh mục không bị cắt bớt âm thầm ở trang đầu —
 * một ô chọn thiếu giống còn tệ hơn một truy vấn nặng hơn chút.
 */
export function useActiveTeaTypes() {
    return useTeaTypes(ACTIVE_TEA_TYPE_PARAMS)
}

// Hằng ở cấp module: tham chiếu ổn định qua mọi lần render, nên khoá cache
// cũng ổn định.
const ACTIVE_TEA_TYPE_PARAMS: ListTeaTypesParams = {
    is_active: true,
    per_page: 200,
}

export function useTeaType(id: string) {
    return useQuery({
        queryKey: teaTypeKeys.detail(id),
        queryFn: async () => {
            const res = await teaTypesApi.getById(id)
            return res.data ?? null
        },
        enabled: Boolean(id),
    })
}

function handleTeaTypeMutationError(err: unknown) {
    if (err instanceof ApiError && err.status === 409) {
        toast.error(m.toast_tea_type_duplicate_code())
        return
    }
    toast.error(m.toast_tea_type_save_error())
}

export function useCreateTeaType() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (data: UpsertTeaTypeRequest) => teaTypesApi.create(data),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: teaTypeKeys.lists() })
            toast.success(m.toast_tea_type_save_success())
        },
        onError: handleTeaTypeMutationError,
    })
}

export function useUpdateTeaType(id: string) {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (data: UpsertTeaTypeRequest) =>
            teaTypesApi.update(id, data),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: teaTypeKeys.lists() })
            void qc.invalidateQueries({ queryKey: teaTypeKeys.detail(id) })
            toast.success(m.toast_tea_type_save_success())
        },
        onError: handleTeaTypeMutationError,
    })
}

export function useDeleteTeaType() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => teaTypesApi.delete(id),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: teaTypeKeys.lists() })
            toast.success(m.toast_tea_type_delete_success())
        },
        onError: () => toast.error(m.toast_tea_type_delete_error()),
    })
}
